import { useContext, useMemo, useReducer } from "react";
import UserContext from "common/Context/UserContext";
import { initialState } from "./Users.model";
import { UsersReducer } from "./Users.reducers";
import { UsersServices } from "./Users.services";
import { USERS_ACTIONS } from "./Users.actions";
import { changeHeaderToken } from "common/HttpClient/http-client";
import { USER_PAGE_TOAST } from "common/Constants/UserPageToast.constants";
import { useUserPageToast } from "common/hooks/UseUserPageToast";
import {
  CUSTOMER_ACCESS_STATUS,
  CUSTOMER_USER_ACCESS_OPERATION,
  CUSTOMER_USER_LIST_ACTION,
  DEFAULT_CUSTOMER_USERS_LIST_FILTERS,
  isCustomerAccessStatus,
  normalizeCustomerUsersListFilters,
} from "./Users.constants";
import { reloadPageIfLoggedInUser } from "common/Utilities/SessionUser.utils";

const CUSTOMER_TOAST = USER_PAGE_TOAST.customer;
const CUSTOMER_USERS_LIST_LOAD_ERROR = "Unable to load users. Please try again.";

const customerUsersListRequestFailed = (response) => {
  const status = Number(response?.status);
  if (Number.isFinite(status) && status >= 400) {
    return true;
  }
  if (response?.error != null && response?.error !== "") {
    return true;
  }
  return false;
};

export const useCustomerUserListActions = (toastHandlers = {}) => {
  const { userProfile } = useContext(UserContext);
  const sessionUserId = userProfile?.profile?.userid;
  const sessionProfile = userProfile?.profile;
  const services = useMemo(() => UsersServices(), []);
  const internalToast = useUserPageToast();
  const showSuccess = toastHandlers?.showSuccess ?? internalToast.showSuccess;
  const showFailure = toastHandlers?.showFailure ?? internalToast.showFailure;

  return useMemo(() => {
    const { onCustomerUserListAction } = UsersActions(() => {}, services, {
      showSuccess,
      showFailure,
      sessionUserId,
      sessionProfile,
    });
    return { onCustomerUserListAction };
  }, [services, showSuccess, showFailure, sessionUserId, sessionProfile]);
};

export const useUsersViewInitialization = (toastHandlers) => {
  const { userProfile } = useContext(UserContext);
  const sessionUserId = userProfile?.profile?.userid;
  const sessionProfile = userProfile?.profile;
  const [state, dispatch] = useReducer(UsersReducer, initialState);
  const services = UsersServices();
  const internalToast = useUserPageToast();
  const showSuccess = toastHandlers?.showSuccess ?? internalToast.showSuccess;
  const showFailure = toastHandlers?.showFailure ?? internalToast.showFailure;
  const actions = useMemo(
    () =>
      UsersActions(dispatch, services, {
        showSuccess,
        showFailure,
        sessionUserId,
        sessionProfile,
      }),
    [showSuccess, showFailure, sessionUserId, sessionProfile]
  );
  return {
    state,
    actions,
    toast: internalToast.toast,
    dismissToast: internalToast.dismissToast,
  };
};

const isUserAccessSaveSuccess = (response) => {
  const httpStatus = response?.status;
  return (
    (httpStatus === 200 || httpStatus === 201 || httpStatus === 204) &&
    response?.error == null
  );
};

const isInvitePostSuccess = (response) => {
  const httpStatus = response?.status;
  return (httpStatus === 200 || httpStatus === 201) && response?.error == null;
};

const normalizeCustomerUserRecord = (item) => {
  const userId = item.userId ?? item.id;
  const roles = Array.isArray(item.roles) ? item.roles : [];
  return {
    ...item,
    userId,
    id: userId,
    email: item.email ?? "",
    firstName: String(item.firstName ?? item.first_name ?? "").trim(),
    lastName: String(item.lastName ?? item.last_name ?? "").trim(),
    phoneNumber: item.phoneNumber ?? item.phone ?? "",
    accountStatus: item.accountStatus ?? "",
    accessId: item.accessId,
    accessStatus: item.accessStatus ?? "",
    inviteId: item.inviteId ?? null,
    roles,
    activatedAt: item.activatedAt ?? null,
  };
};

const UsersActions = (dispatch, services, toastHandlers = {}) => {
  const { showSuccess, showFailure, sessionUserId, sessionProfile } = toastHandlers;

  const patchCustomerUserAccessOperation = async (
    customerId,
    customerCode,
    user,
    { operation, roles, reason = null, successMessage, failureMessage }
  ) => {
    const userId = user?.userId ?? user?.id;
    if (!customerId || userId == null || String(userId).trim() === "") {
      return {
        ok: false,
        error: "Missing customer or user identifier.",
      };
    } 
    if (customerCode) {
      changeHeaderToken(sessionUserId, customerCode);
    }
    const body = {
      operation,
      reason,
    };
    if (roles !== undefined) {
      body.roles = (roles ?? []).map((role) => String(role)).filter(Boolean);
    }
    try {
      const response = await services.patchUserCustomerAccess(
        userId,
        customerId,
        body
      );
      if (!isUserAccessSaveSuccess(response)) {
        showFailure?.(response?.error, failureMessage);
        return {
          ok: false,
          error:
            typeof response?.error === "string"
              ? response.error
              : failureMessage,
        };
      }
      showSuccess?.(successMessage);
      if (reloadPageIfLoggedInUser(sessionProfile, userId)) {
        return { ok: true };
      }
      return { ok: true };
    } catch {
      showFailure?.(null, CUSTOMER_TOAST.unexpectedError);
      return {
        ok: false,
        error: CUSTOMER_TOAST.unexpectedError,
      };
    }
  };

  const getAllUsers = async (customerId, customerCode, options = {}) => {
    const {
      page = 0,
      size = 25,
      filters = DEFAULT_CUSTOMER_USERS_LIST_FILTERS,
      callback = () => {},
    } = options;
    const listFilters = { ...DEFAULT_CUSTOMER_USERS_LIST_FILTERS, ...filters };
    dispatch({
      type: USERS_ACTIONS.LIST_FILTERS,
      payload: listFilters,
    });
    if (!customerId) {
      dispatch({
        type: USERS_ACTIONS.ERROR,
        payload:
          "Missing customer identifier. Please select a customer from the header.",
      });
      return;
    }
    if (customerCode) {
      changeHeaderToken(sessionUserId, customerCode);
    }
    dispatch({
      type: USERS_ACTIONS.LOADING,
      payload: true,
    });
    try {
      const response = await services.getAllUsers(
        customerId,
        page,
        size,
        listFilters
      );
      if (customerUsersListRequestFailed(response) || response?.data == null) {
        dispatch({
          type: USERS_ACTIONS.ERROR,
          payload: response?.error ?? CUSTOMER_USERS_LIST_LOAD_ERROR,
        });
        return;
      }
      const listResponseBody = response.data;
      const rawList = Array.isArray(listResponseBody)
        ? listResponseBody
        : listResponseBody?.data ?? listResponseBody?.content ?? [];
      const usersFromApi = rawList.map(normalizeCustomerUserRecord);
      const serverPagination = listResponseBody?.pagination;
      const mapCustomerUsersPagination = (
        apiPagination,
        requestedPage,
        requestedSize
      ) => {
        const p = apiPagination ?? {};
        const normalizedSize =
          Number(p.pageSize ?? p.size ?? requestedSize ?? 25) || 25;
        const totalCount = Number(p.totalCount ?? 0);
        let normalizedPage;
        if (p.page != null) {
          normalizedPage = Math.max(0, Number(p.page));
        } else if (p.pageIndex != null) {
          normalizedPage = Math.max(0, Number(p.pageIndex));
        } else {
          normalizedPage = Math.max(0, Number(requestedPage) || 0);
        }
        return {
          page: normalizedPage,
          size: normalizedSize,
          totalCount,
          totalPages: Number(
            p.totalPages ??
              (totalCount === 0 ? 0 : Math.ceil(totalCount / normalizedSize))
          ),
          hasNext: Boolean(p.hasNext),
          hasPrevious: Boolean(p.hasPrevious),
        };
      };
      const pagination = serverPagination
        ? mapCustomerUsersPagination(serverPagination, page, size)
        : {
            page,
            size,
            totalCount: usersFromApi.length,
            totalPages: usersFromApi.length > 0 ? 1 : 0,
            hasNext: false,
            hasPrevious: false,
          };
      dispatch({
        type: USERS_ACTIONS.USERS,
        payload: {
          list: usersFromApi,
          pagination,
          listFilters,
        },
      });
      callback();
    } catch {
      dispatch({
        type: USERS_ACTIONS.ERROR,
        payload: CUSTOMER_USERS_LIST_LOAD_ERROR,
      });
    } finally {
      dispatch({
        type: USERS_ACTIONS.LOADING,
        payload: false,
      });
    }
  };

  return {
    getAllUsers,
    onListFiltersChange: async (
      customerId,
      customerCode,
      rawColumnFilters,
      pageSize = 25
    ) => {
      if (!customerId || !customerCode) {
        return;
      }
      const listFilters = normalizeCustomerUsersListFilters(rawColumnFilters);
      await getAllUsers(customerId, customerCode, {
        page: 0,
        size: pageSize,
        filters: listFilters,
      });
    },
    onCustomerUserListAction: async (customerId, customerCode, user, action, roles, isUserAccessEdit = false) => {
      if (!customerId || !customerCode) {
        return { ok: false, error: "Missing customer context." };
      }
      if (!isUserAccessEdit && customerCode) {
        changeHeaderToken(sessionUserId, customerCode);
      }
      const selectedRoles = (roles ?? user?.roles ?? [])
        .map((role) => String(role))
        .filter(Boolean);
      switch (action) {
        case CUSTOMER_USER_LIST_ACTION.REVOKE_ACCESS:
          return patchCustomerUserAccessOperation(customerId, customerCode, user, {
            operation: CUSTOMER_USER_ACCESS_OPERATION.REVOKE,
            roles: selectedRoles,
            successMessage: CUSTOMER_TOAST.accessRevokeSuccess,
            failureMessage: CUSTOMER_TOAST.accessUpdateFailure,
            reason: "revoked by administrator",
          });
        case CUSTOMER_USER_LIST_ACTION.RESTORE_ACCESS:
          return patchCustomerUserAccessOperation(customerId, customerCode, user, {
            operation: CUSTOMER_USER_ACCESS_OPERATION.RESTORE,
            roles: selectedRoles,
            successMessage: CUSTOMER_TOAST.accessRestoreSuccess,
            failureMessage: CUSTOMER_TOAST.accessUpdateFailure,
            reason: "restored by administrator",
          });
        case CUSTOMER_USER_LIST_ACTION.RESEND: {
          try {
            const response = await services.resendCustomerUserInvite(
              user.inviteId,
              {});
            const httpStatus = response?.status;
            const ok =
              (httpStatus === 200 ||
                httpStatus === 201 ||
                httpStatus === 204) &&
              response?.error == null;
            if (!ok) {
              showFailure?.(response?.error, CUSTOMER_TOAST.resendFailure);
              return {
                ok: false,
                error:
                  typeof response?.error === "string"
                    ? response.error
                    : CUSTOMER_TOAST.resendFailure,
              };
            }
            showSuccess?.(CUSTOMER_TOAST.resendSuccess);
            return { ok: true };
          } catch {
            showFailure?.(null, CUSTOMER_TOAST.unexpectedError);
            return {
              ok: false,
              error: CUSTOMER_TOAST.unexpectedError,
            };
          }
        }
        default:
          return { ok: false, error: "Unknown action." };
      }
    },
    checkInviteEmailExists: async (email, customerId) => {
      const response = await services.checkInviteEmailExists(email, customerId);
      if (response?.error) {
        return { exists: false, error: response.error };
      }
      return { exists: Boolean(response?.data?.exists) };
    },
    inviteCustomerUser: async (customerId, customerCode, invitePayload) => {
      if (!customerId) {
        showFailure?.(null, CUSTOMER_TOAST.missingCustomer);
        return false;
      }
      if (customerCode) {
        changeHeaderToken(sessionUserId, customerCode);
      }
      try {
        const body = {
          email: String(invitePayload?.email ?? "").trim(),
          roles: Array.isArray(invitePayload?.roles)
            ? invitePayload.roles.filter(Boolean)
            : [],
          sendInviteEmail: invitePayload?.sendInviteEmail !== false,
        };
        const response = await services.postCustomerUserInvite(
          customerId,
          body
        );
        if (!isInvitePostSuccess(response)) {
          showFailure?.(response?.error, CUSTOMER_TOAST.inviteFailure);
          return false;
        }
        showSuccess?.(CUSTOMER_TOAST.inviteSuccess);
        return true;
      } catch {
        showFailure?.(null, CUSTOMER_TOAST.unexpectedError);
        return false;
      }
    },
    assignCustomerUserRoles: async (customerId, customerCode, user, roles) =>
      patchCustomerUserAccessOperation(customerId, customerCode, user, {
        operation: CUSTOMER_USER_ACCESS_OPERATION.ASSIGN_ROLES,
        roles,
        successMessage: CUSTOMER_TOAST.rolesSuccess,
        failureMessage: CUSTOMER_TOAST.rolesFailure,
        reason: "assigned by administrator",
      }),
    patchCustomerUserAccess: async (customerId, customerCode, payload) => {
      if (!customerId || payload?.accessId == null) {
        return {
          ok: false,
          error: "Missing customer or access record.",
        };
      }
      if (customerCode) {
        changeHeaderToken(sessionUserId, customerCode);
      }
      const body = {
        accessId: payload.accessId,
        ...(payload.accessStatus != null && payload.accessStatus !== ""
          ? { accessStatus: String(payload.accessStatus) }
          : {}),
      };
      const hasStatus =
        body.accessStatus != null && String(body.accessStatus).trim() !== "";
      if (!hasStatus) {
        return {
          ok: false,
          error: "Nothing to update.",
        };
      }
      try {
        const response = await services.patchCustomerUserAccess(
          customerId,
          body
        );
        if (!isUserAccessSaveSuccess(response)) {
          showFailure?.(response?.error, CUSTOMER_TOAST.accessUpdateFailure);
          return {
            ok: false,
            error:
              typeof response?.error === "string"
                ? response.error
                : CUSTOMER_TOAST.accessUpdateFailure,
          };
        }
        const status = String(body.accessStatus ?? "").trim();
        if (isCustomerAccessStatus(status, CUSTOMER_ACCESS_STATUS.REVOKED)) {
          showSuccess?.(CUSTOMER_TOAST.accessRevokeSuccess);
        } else if (
          isCustomerAccessStatus(status, CUSTOMER_ACCESS_STATUS.ACTIVE)
        ) {
          showSuccess?.(CUSTOMER_TOAST.accessGrantSuccess);
        } else {
          showSuccess?.(CUSTOMER_TOAST.accessGrantSuccess);
        }
        if (reloadPageIfLoggedInUser(sessionProfile, payload.userId)) {
          return { ok: true };
        }
        return { ok: true };
      } catch {
        showFailure?.(null, CUSTOMER_TOAST.unexpectedError);
        return {
          ok: false,
          error: CUSTOMER_TOAST.unexpectedError,
        };
      }
    },
    removeError: () => {
      dispatch({
        type: USERS_ACTIONS.ERROR,
        payload: null,
      });
    },
  };
};
